import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, Answer } from '../../types';
import { useTestStore } from '../../store/testStore';

interface QuestionCardProps {
  question: Question;
  questionNumber: number;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, questionNumber }) => {
  const { answers, setAnswer } = useTestStore();
  const currentAnswer = answers[question.id]?.value;
  
  const handleOptionSelect = (value: number) => {
    setAnswer(question.id, value);
  };
  
  const renderOptions = () => {
    switch (question.type) {
      case 'multiple-choice':
      case 'yes-no':
        return (
          <div className="space-y-3 mt-4">
            {question.options.map(option => (
              <motion.button
                key={option.id}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                  currentAnswer === option.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
                onClick={() => handleOptionSelect(option.value)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {option.text}
              </motion.button>
            ))}
          </div>
        );
        
      case 'likert':
        return (
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              {question.options.map(option => (
                <div key={option.id} className="text-xs text-gray-500 text-center px-1">
                  {option.text.split(' - ')[0]}
                </div>
              ))}
            </div>
            
            <div className="flex justify-between gap-2">
              {question.options.map(option => (
                <motion.button
                  key={option.id}
                  className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                    currentAnswer === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  onClick={() => handleOptionSelect(option.value)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {option.text.split(' - ')[0]}
                </motion.button>
              ))}
            </div>
            
            <div className="flex justify-between mt-2">
              <span className="text-xs text-gray-500">Very Low</span>
              <span className="text-xs text-gray-500">Low</span>
              <span className="text-xs text-gray-500">Moderate</span>
              <span className="text-xs text-gray-500">High</span>
              <span className="text-xs text-gray-500">Very High</span>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={question.id}
        className="bg-white rounded-xl shadow-card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-start">
          <span className="flex-shrink-0 bg-primary-100 text-primary-700 font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3">
            {questionNumber}
          </span>
          <h3 className="text-lg font-medium text-gray-800">
            {question.text}
          </h3>
        </div>
        
        {renderOptions()}
      </motion.div>
    </AnimatePresence>
  );
};

export default QuestionCard;